import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

const navLinks = [
  { label: "Problem", href: "#problem" },
  { label: "Solution", href: "#solution" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Demo", href: "#demo" },
  { label: "Features", href: "#features" },
  { label: "Use Cases", href: "#use-cases" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-stripe-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-stripe-primary to-cyan-400 flex items-center justify-center shadow-md shadow-stripe-primary/20">
              <span className="text-white font-heading font-bold text-sm">S</span>
            </div>
            <span className="font-heading font-semibold text-lg text-navy tracking-tight">
              SkillsimAI
            </span>
          </a>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="px-4 py-2 text-sm text-stripe-text-secondary hover:text-navy transition-colors duration-200 rounded-full hover:bg-slate-100"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Button
              data-testid="nav-cta-button"
              onClick={() => scrollTo("#cta")}
              className="bg-stripe-primary hover:bg-stripe-primary-hover text-white rounded-full px-6 text-sm font-medium shadow-md shadow-stripe-primary/20 hover:shadow-lg hover:shadow-stripe-primary/25 transition-all duration-300"
            >
              Request Early Access
            </Button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-navy p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-stripe-border pb-6">
          <div className="px-6 pt-4 space-y-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="block w-full text-left px-4 py-3 text-stripe-text-secondary hover:text-navy hover:bg-slate-50 rounded-lg text-sm transition-colors"
              >
                {link.label}
              </button>
            ))}
            <Button
              onClick={() => scrollTo("#cta")}
              className="w-full mt-4 bg-stripe-primary hover:bg-stripe-primary-hover text-white rounded-full"
            >
              Request Early Access
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
